import React, { CSSProperties } from "react";
import "./widget.css";
import LoadingVeil from "./LoadingVeil";

export type WidgetStatus = "success" | "warning" | "error" | "info" | "default";

export interface WidgetProps {
  title: string;
  value: number | string | React.ReactNode;
  status?: WidgetStatus;
  statusText?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  trend?: {
    value: number;
    isPositive?: boolean;
  };
  valuePrefix?: string;
  valueSuffix?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  compact?: boolean;
  inline?: boolean;
}

export const Widget: React.FC<WidgetProps> = ({
  title,
  value,
  status,
  statusText,
  subtitle,
  icon,
  loading = false,
  trend,
  valuePrefix = "",
  valueSuffix = "",
  className = "",
  style,
  onClick,
  compact = false,
  inline = false,
}) => {
  const widgetClasses = [
    "modern-widget",
    status ? `status-${status}` : "",
    onClick ? "clickable" : "",
    loading ? "loading" : "",
    compact ? "compact" : "",
    inline ? "inline" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const badgeClass = status ? `badge-${status}` : "";

  return (
    <div className={widgetClasses} style={style} onClick={onClick}>
      {/* the shared veil, so a figure loads the same way here as anywhere */}
      <LoadingVeil loading={loading} label={`Loading ${title}`} size={compact || inline ? 'small' : 'default'}>
        <div className="modern-widget-body">
          {/* Header */}
          <div className="modern-widget-header">
            <div className="modern-widget-title-wrapper">
              {icon && <div className="modern-widget-icon">{icon}</div>}
              <div className="modern-widget-title">{title}</div>
            </div>
            {status && statusText && (
              <div className={`modern-widget-badge ${badgeClass}`}>
                {statusText}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="modern-widget-content">
            <div className="modern-widget-value-wrapper">
              <div className="modern-widget-value">
                {valuePrefix}
                {typeof value === "number" ? value.toLocaleString() : value}
                {valueSuffix}
              </div>
              {trend && (
                <div
                  className={`modern-widget-trend ${
                    trend.isPositive !== false ? "trend-positive" : "trend-negative"
                  }`}
                >
                  <span className="modern-widget-trend-icon">
                    {trend.isPositive !== false ? "↑" : "↓"}
                  </span>
                  <span>{Math.abs(trend.value)}%</span>
                </div>
              )}
            </div>

            {subtitle && <div className="modern-widget-subtitle">{subtitle}</div>}
          </div>
        </div>
      </LoadingVeil>
    </div>
  );
};

export default Widget;